<script setup lang="ts">
import { onMounted } from 'vue'
import { RouterView, RouterLink } from 'vue-router'

onMounted(() => {
  document.getElementById('app')?.removeAttribute('aria-busy')
})
</script>

<template>
  <a class="skip-link" href="#main-content">Skip to main content</a>

  <header class="site-header" role="banner">
    <div class="site-header__brand">
      <RouterLink to="/" class="site-header__title" aria-label="Ukraine Energy Map — home">
        Ukraine Energy Map
      </RouterLink>
      <p class="site-header__tagline">An evidentiary instrument.</p>
    </div>
    <nav class="site-header__nav" aria-label="Primary">
      <RouterLink to="/" class="site-header__link">Dashboard</RouterLink>
      <RouterLink to="/methodology" class="site-header__link">Methodology</RouterLink>
      <RouterLink to="/downloads" class="site-header__link">Cite</RouterLink>
      <a href="/docs/" class="site-header__link">Learn</a>
    </nav>
  </header>

  <main id="main-content" class="site-main" tabindex="-1">
    <RouterView />
  </main>

  <footer class="site-footer" role="contentinfo">
    <p class="site-footer__attribution">
      Data from DiXi Group reports, OpenStreetMap contributors, and DeepState.
      For research purposes only.
    </p>
    <p class="site-footer__version mono">
      v1.0.0
    </p>
  </footer>
</template>

<style>
.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: var(--color-text);
  color: var(--color-bg);
  padding: var(--space-2) var(--space-4);
  z-index: var(--z-modal);
  text-decoration: none;
  font-weight: 600;
  font-size: var(--font-size-sm);
  transform: translateY(-100%);
  transition: transform var(--motion-duration-fast) var(--motion-ease);
}
.skip-link:focus {
  transform: translateY(140%);
}

.site-header {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6) var(--space-8);
  border-bottom: 1px solid var(--color-border);
}

@media (min-width: 768px) {
  .site-header {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
  }
}

.site-header__brand {
  display: flex;
  align-items: baseline;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.site-header__title {
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text);
  text-decoration: none;
  letter-spacing: var(--letter-spacing-tight);
  line-height: var(--line-height-tight);
}
.site-header__title:hover {
  color: var(--color-accent);
}

.site-header__tagline {
  margin: 0;
  font-size: var(--font-size-sm);
  font-style: italic;
  color: var(--color-text-dim);
}

.site-header__nav {
  display: flex;
  gap: var(--space-6);
}

.site-header__link {
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: 600;
  padding: var(--space-2) var(--space-1);
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  border-bottom: 1px solid transparent;
  transition: color var(--motion-duration-fast) var(--motion-ease),
              border-color var(--motion-duration-fast) var(--motion-ease);
}
.site-header__link:hover {
  color: var(--color-text);
}
.site-header__link.router-link-active {
  color: var(--color-text);
  border-bottom-color: var(--color-text-muted);
}

.site-main {
  min-height: calc(100vh - 10rem);
  padding: 0;
}

.site-footer {
  padding: var(--space-6) var(--space-8);
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--space-4);
}

.site-footer__attribution {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-dim);
  max-width: 60ch;
}

.site-footer__version {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-dim);
}
</style>
